import {observer} from "mobx-react";
import {useStore} from "../../../../../hooks";
import React, {useCallback, useEffect, useMemo, useState, useRef} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle, IconButton,
  TextField,
  Typography,
  Fab,
} from "@mui/material";
import {Icon} from "../../../../../elements";
import {
  ActionButton,
  ChatArea, ContactHeader,
  ConversationContent,
  ConversationHeader,
  ConversationViewWrapper, PendingApprovalActions
} from "../MarketingTab/MarketingTab.styled";
import dayjs from "dayjs";
import Avatar from "@mui/material/Avatar";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';
import DOMPurify from 'dompurify';
import { MessageBody } from "../../ConversationView.styled";
import { ConversationListWithTabs, ApprovedMessagesContext } from "../ConversationList/ConversationList";
import ConversationListStore from "../../../../../store/conversations/ConversationListStore";
import {useUserCheckPermissions} from "../../../../../hooks/useUserCheckPermissions";
import {PermissionCodes} from "../../../../../utils/enums";

export const ConversationView = observer(({
  campaign,
  onClose,
  selectedConversation,
  onSelectConversation
}) => {
  const { marketingStore } = useStore();
  const conversationListStore = new ConversationListStore();
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editedMessageContent, setEditedMessageContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [, setIsLoading] = useState(false);
  const [, setError] = useState(null);
  const { checkPermission } = useUserCheckPermissions();
  
  const timelineContainerRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  
  const { approvedIds, addApprovedMessage } = React.useContext(ApprovedMessagesContext);
  
  const isMessageApproved = useCallback((messageId) => {
    if (window.ANNAIMS_APPROVED_MESSAGES && window.ANNAIMS_APPROVED_MESSAGES.has(messageId)) {
      return true;
    }
    if (approvedIds && approvedIds.has && approvedIds.has(messageId)) {
      return true;
    }
    return false;
  }, [approvedIds]);

  const markMessageAsApproved = useCallback((messageId) => {
    if (window.ANNAIMS_APPROVED_MESSAGES) {
      window.ANNAIMS_APPROVED_MESSAGES.add(messageId);
      
      try {
        localStorage.setItem('annaims_approved_messages', 
          JSON.stringify([...window.ANNAIMS_APPROVED_MESSAGES]));
        
        window.dispatchEvent(new CustomEvent('annaims_message_approved', { 
          detail: { messageId } 
        }));
      } catch (err) {
      }
    }
    
    // Add to context if available
    if (addApprovedMessage && typeof addApprovedMessage === 'function') {
      addApprovedMessage(messageId);
    }
  }, [addApprovedMessage]);

  const formattedMessages = useMemo(() => {
    if (!selectedConversation?.messages) return [];

    const formatted = selectedConversation.messages.map(msg => {
      // Check if message is approved from localStorage or context
      const approved = isMessageApproved(msg.id);
      
      // Force SENT status for approved messages regardless of backend status
      const status = approved ? 'SENT' : (msg.status || 'PENDING_SEND');
      
      return {
        ...msg,
        id: msg.id || `msg-${Date.now()}-${Math.random()}`,
        sender: msg.sender || (msg.isAgent ? 'agent' : 'customer'),
        message: msg.message || msg.content || '',
        insert_date: msg.insert_date || msg.timestamp || msg.created_at || new Date().toISOString(),
        channel: msg.channel || 'campaign',
        isAgent: msg.isAgent || msg.sender === 'agent',
        status: status,
        approvedAt: msg.approvedAt || (status === 'SENT' ? new Date().toISOString() : null)
      };
    });
    
    return formatted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?.messages, approvedIds, isMessageApproved]);

  useEffect(() => {
    if (selectedConversation?.id) {
      const loadMessages = async () => {
        setIsLoadingMessages(true);
        try {
          if (typeof marketingStore.fetchConversationMessages === 'function') {
            await marketingStore.fetchConversationMessages(
              campaign.id,
              selectedConversation.id
            );
          }
        } catch (error) {
          // Silently handle error
        } finally {
          setIsLoadingMessages(false);
        }
      };

      loadMessages();
    }
  }, [campaign.id, selectedConversation?.id, marketingStore]);

  const handleEditMessage = useCallback((message) => {
    setEditingMessage(message);
    setEditedMessageContent(message.message);
    setIsEditModalOpen(true);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingMessage) return;

    try {
      // Update local state first for immediate feedback
      const updatedMessages = formattedMessages.map(msg =>
        msg.id === editingMessage.id
          ? {
            ...msg,
            message: editedMessageContent,
            // Maintain the current status instead of changing it
            status: msg.status
          }
          : msg
      );

      // Update the conversation in the parent component
      onSelectConversation({
        ...selectedConversation,
        messages: updatedMessages
      });

      // Call the API to update the message
      await marketingStore.putCampaignMessage(
        editingMessage.id,
        campaign.id,
        editedMessageContent
      );

      // Clear editing state
      setEditingMessage(null);
      // Close the modal
      setIsEditModalOpen(false);

      // Show success message
      marketingStore.modal.show({
        modal: "generalMessageModal",
        title: "Success!",
        message: "Message has been updated successfully."
      });
    } catch (error) {
      // Show error message
      marketingStore.modal.show({
        modal: "generalMessageModal",
        title: "Error",
        message: "Failed to update the message. Please try again."
      });
    } finally {
      // Always close the modal and clear editing state
      setIsEditModalOpen(false);
      setEditingMessage(null);
    }
  }, [editingMessage, editedMessageContent, formattedMessages, selectedConversation, onSelectConversation, campaign.id, marketingStore]);

  const handleApproveMessage = useCallback(async (message) => {
    try {
      setIsLoading(true);
      setError(null);

      const approvedAt = new Date().toISOString();
      
      // First, immediately mark as approved in localStorage to ensure UI state is preserved
      markMessageAsApproved(message.id);
      
      // Force refresh of the badge count
      window.dispatchEvent(new Event('annaims_message_approved'));
      
      let messageContent = message.message;
      try {
        if (typeof message.message === 'string' && (message.message.startsWith('{') || message.message.startsWith('['))) {
          const parsedMessage = JSON.parse(message.message);
          if (parsedMessage.message) {
            messageContent = parsedMessage.message;
          }
        }
      } catch (e) {
        // If parsing fails, use the original message
      }
      
      const updatedMessage = {
        ...message,
        message: messageContent,
        status: "SENT",
        approvedAt
      };

      const response = await conversationListStore.updateMessage(
        message.id,
        campaign.id,
        messageContent,
        'SENT',
        approvedAt
      );

      if (!response) {
        throw new Error("Failed to approve message");
      }

      // Create a new conversation object with forced SENT status for the UI
      const updatedConversations = campaign.conversations.map(conv => {
        if (conv.id === selectedConversation.id) {
          return {
            ...conv,
            messages: conv.messages.map(msg => 
              msg.id === message.id ? updatedMessage : msg
            )
          };
        }
        return conv;
      });

      // Create a new conversation object with forced SENT status for the UI
      const conversationWithApprovedMessage = {
        ...updatedConversations.find(conv => conv.id === selectedConversation.id),
        messages: updatedConversations
          .find(conv => conv.id === selectedConversation.id)
          .messages.map(msg => ({
            ...msg,
            // Force SENT status for the approved message
            status: msg.id === message.id ? 'SENT' : msg.status,
            approvedAt: msg.id === message.id ? approvedAt : msg.approvedAt
          }))
      };
      
      // Update the selected conversation with the approved message
      onSelectConversation(conversationWithApprovedMessage);
    } catch (error) {
      setError("Failed to approve message. Please try again.");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign, selectedConversation, onSelectConversation, markMessageAsApproved]);

  // Function to scroll to the bottom of the messages
  const scrollToBottom = useCallback(() => {
    if (timelineContainerRef.current) {
      const container = timelineContainerRef.current;
      
      // Wait a short moment for DOM to stabilize
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
        setShowScrollButton(false);
      }, 100);
    }
  }, []);

  // Add event listener to the timeline container to show/hide the scroll button
  useEffect(() => {
    const container = timelineContainerRef.current;
    if (!container) return;
    
    const handleScroll = () => {
      // Show button when user has scrolled up at least 300px from bottom
      const isScrolledUp = container.scrollHeight - container.scrollTop - container.clientHeight > 300;
      setShowScrollButton(isScrolledUp);
    };
    
    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineContainerRef.current]);

  // Scroll to bottom when selected conversation changes
  useEffect(() => {
    if (selectedConversation) {
      setTimeout(scrollToBottom, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?.id, scrollToBottom]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (formattedMessages?.length > 0) {
      setTimeout(scrollToBottom, 300);
    }
  }, [formattedMessages, scrollToBottom]);

  // Scroll to bottom after loading completes
  useEffect(() => {
    if (!isLoadingMessages && selectedConversation) {
      setTimeout(scrollToBottom, 300);
    }
  }, [isLoadingMessages, selectedConversation, scrollToBottom]);

  const renderEditModal = () => (
    <Dialog
      open={isEditModalOpen}
      onClose={() => setIsEditModalOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Edit Message
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          multiline
          rows={6}
          value={editedMessageContent}
          onChange={(e) => setEditedMessageContent(e.target.value)}
          margin="normal"
          variant="outlined"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setIsEditModalOpen(false)}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveEdit}
          variant="contained"
          color="primary"
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );

  const createMarkup = (content) => {
    return { __html: DOMPurify.sanitize(content) };
  };

  const isHTML = (str) => {
    const doc = new DOMParser().parseFromString(str, "text/html");
    return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
  };

  const renderMessageContent = (message) => {
    // First, try to extract message content if it's accidentally JSON
    let messageContent = message;
    
    // Enhanced type validation
    if (typeof message !== 'string') {
      console.error('Message is not a string:', message);
      // Try to extract string from object
      if (typeof message === 'object' && message !== null) {
        if (message.message) messageContent = message.message;
        else if (message.content) messageContent = message.content;
        else if (message.text) messageContent = message.text;
        else {
          return (
            <div style={{ color: 'red', padding: '10px', border: '1px solid red' }}>
              Error rendering message: Invalid message format
              <details style={{ marginTop: '10px', fontSize: '12px' }}>
                <summary>Debug info</summary>
                <pre>{JSON.stringify(message, null, 2)}</pre>
              </details>
            </div>
          );
        }
      } else {
        return <div style={{ color: 'red' }}>Unable to display message</div>;
      }
    }
    
    try {
      if (typeof messageContent === 'string' && (messageContent.startsWith('{') || messageContent.startsWith('['))) {
        const parsedMessage = JSON.parse(messageContent);
        if (parsedMessage.message) {
          // Extract the actual message from the JSON
          messageContent = parsedMessage.message;
        }
      }
    } catch (e) {
      // If parsing fails, use the original message
    }

    // Final check to ensure messageContent is a string
    if (typeof messageContent !== 'string') {
      console.error('Message content is still not a string after extraction:', messageContent);
      return <div style={{ color: 'red' }}>Unable to display message</div>;
    }

    if (isHTML(messageContent)) {
      return (
        <div 
          dangerouslySetInnerHTML={createMarkup(messageContent)}
          className="html-content"
        />
      );
    }

    return (
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm,
          remarkParse,
          [remarkRehype, {
            singleTilde: true,
            stringLength: true,
            tablePipeAlign: true,
            tableCellPadding: true
          }]
        ]}
        rehypePlugins={[
          rehypeRaw,
          rehypeStringify,
          [rehypeSanitize, {
            allowElements: [
              'p', 'br', 'b', 'i', 'em', 'strong', 'a', 'img',
              'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
              'ul', 'ol', 'li', 'blockquote', 'code', 'pre',
              'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
              'div', 'span', 'del'
            ],
            allowAttributes: {
              'a': ['href', 'target', 'rel'],
              'img': ['src', 'alt', 'title'],
              'td': ['align'],
              'th': ['align'],
              '*': ['class']
            }
          }]
        ]}
      >
        {messageContent}
      </ReactMarkdown>
    );
  };

  return (
    <ConversationViewWrapper>
      <ConversationHeader>
        <div className="header-left">
          <Typography variant="h6">
            {campaign.name}
          </Typography>
        </div>
        <IconButton onClick={onClose}>
          <Icon name="close" />
        </IconButton>
      </ConversationHeader>

      <ConversationContent>
        <ConversationListWithTabs
          conversations={campaign.conversations}
          selectedConversation={selectedConversation}
          onSelectConversation={onSelectConversation}
          isLoading={marketingStore.isLoadingConversations}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />

        <ChatArea>
          {selectedConversation ? (
            <>
              <ContactHeader>
                <div className="contact-info">
                  <Typography variant="h6">
                    {selectedConversation.contact_name}
                  </Typography>
                  <Typography className="contact-details">
                    {selectedConversation.contact_email || 'No email'}
                    {selectedConversation.contact_phone && ` • ${selectedConversation.contact_phone}`}
                  </Typography>
                </div>
              </ContactHeader>

              <div className="timeline-container" ref={timelineContainerRef}>
                {isLoadingMessages ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress size={24} />
                  </Box>
                ) : formattedMessages.length > 0 ? (
                  formattedMessages.map(message => (
                    <div
                      key={message.id}
                      className={`message-content ${message.isAgent ? 'agent-message' : 'user-message'}`}
                    >
                      <div className="message-container" style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        flexDirection: message.isAgent ? 'row' : 'row-reverse'
                      }}>
                        <Avatar
                          sx={{
                            width: 41,
                            height: 41,
                            bgcolor: message.isAgent ? '#E0E7FF' : '#F0FDF4',
                            color: message.isAgent ? '#4F46E5' : '#16A34A',
                            fontSize: message.role === 'AGENT' ? '8px' : '14px'
                          }}
                        >
                          {message.role === 'AGENT'
                            ? 'ANNAIMS'
                            : message.sender?.slice(0, 2).toUpperCase() || 'C'
                          }
                        </Avatar>

                        <div style={{ flex: 1 }}>
                          <MessageBody isAgent={message.isAgent}>
                            {renderMessageContent(message.message)}
                          </MessageBody>

                          {message.isAgent && (
                            <>
                              {message.status === 'SENT' ? (
                                <div className="approved-tag" style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '4px 8px',
                                  backgroundColor: '#F0FDF4',
                                  borderRadius: '4px',
                                  color: '#16A34A',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  marginTop: '8px'
                                }}>
                                  <Icon
                                    name="check"
                                    style={{
                                      color: '#16A34A',
                                      width: '14px',
                                      height: '14px'
                                    }}
                                  />
                                  Approved {dayjs(message.approvedAt).format('MM/DD/YYYY')}
                                </div>
                              ) : message.status === 'PENDING_SEND' && (
                                <PendingApprovalActions>
                                  <span className="pending-text">Pending Approval</span>
                                  <div className="actions">

                                    {checkPermission(PermissionCodes.EDIT_MESSAGES, "UPDATE") && (
                                      <ActionButton
                                        action="edit"
                                        onClick={() => handleEditMessage(message)}
                                      >
                                        <Icon
                                          name="editPencil"
                                          style={{ color: '#666666' }}
                                          width="14px"
                                          height="14px"
                                        />
                                        Edit
                                      </ActionButton>
                                    )}

                                    {checkPermission(PermissionCodes.APPROVE_MESSAGES, "UPDATE") && (
                                      <ActionButton
                                        action="approve"
                                        onClick={() => handleApproveMessage(message)}
                                      >
                                        <Icon
                                          name="check"
                                          style={{
                                            color: '#16A34A',
                                            fill: '#16A34A',
                                            stroke: '#16A34A'
                                          }}
                                          width="14px"
                                          height="14px"
                                        />
                                        Approve
                                      </ActionButton>
                                    )}
                                  </div>
                                </PendingApprovalActions>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <Typography color="textSecondary">
                      No messages in this conversation yet.
                    </Typography>
                  </Box>
                )}
              </div>

              {showScrollButton && (
                <Fab
                  size="small"
                  color="primary"
                  aria-label="scroll to bottom"
                  onClick={scrollToBottom}
                  sx={{
                    position: 'absolute',
                    bottom: '20px',
                    right: '20px',
                    boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
                    zIndex: 10,
                  }}
                >
                  <Icon name="arrowDown" width="20px" height="20px" />
                </Fab>
              )}

              {renderEditModal()}
            </>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              padding={3}
            >
              <Icon name="chat" height="48px" width="48px" style={{ opacity: 0.5, marginBottom: 16 }} />
              <Typography variant="h6" color="textSecondary">
                Select a conversation
              </Typography>
              <Typography color="textSecondary">
                Choose a conversation from the list to view messages
              </Typography>
            </Box>
          )}
        </ChatArea>
      </ConversationContent>
    </ConversationViewWrapper>
  );
});